import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from "react";

import type { AiInsightsRaidTab } from "../api/aiInsights";

/**
 * Lifts the RAID page's currently-active sub-tab (Risks & Issues / Assumptions
 * / Dependencies) so the global insights overlay -- mounted as a page-level
 * sibling in Layout, not a child of RaidPage -- can request AI Insights for
 * whichever RAID tab the user actually has open, instead of a fixed tab.
 * Defaults to "risks-issues" (RaidPage's own default active tab) so the
 * overlay has a sensible value before RaidPage mounts/reports its state.
 */
interface RaidActiveTabContextValue {
  activeRaidTab: AiInsightsRaidTab;
  setActiveRaidTab: (tab: AiInsightsRaidTab) => void;
}

const RaidActiveTabContext = createContext<RaidActiveTabContextValue | null>(null);

export function RaidActiveTabProvider({ children }: { children: ReactNode }) {
  const [activeRaidTab, setActiveRaidTabState] = useState<AiInsightsRaidTab>("risks-issues");

  const setActiveRaidTab = useCallback((tab: AiInsightsRaidTab) => {
    setActiveRaidTabState(tab);
  }, []);

  const value = useMemo(() => ({ activeRaidTab, setActiveRaidTab }), [activeRaidTab, setActiveRaidTab]);

  return <RaidActiveTabContext.Provider value={value}>{children}</RaidActiveTabContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components -- context files intentionally co-locate Provider + hook
export function useRaidActiveTab(): RaidActiveTabContextValue {
  const ctx = useContext(RaidActiveTabContext);
  if (ctx == null) {
    throw new Error("useRaidActiveTab must be used within RaidActiveTabProvider");
  }
  return ctx;
}
